import React, { Component, ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    isChunkError: boolean;
    errorName?: string;
    errorMessage?: string;
}

/**
 * ErrorBoundary that catches ChunkLoadError (caused by stale JS chunks after redeployment)
 * and automatically reloads the page once to fetch fresh assets.
 * 
 * If it's a runtime application error (not a chunk loading error), it shows a friendly
 * fallback error page with option to reload or return home, without incorrectly claiming
 * that a new app version was deployed.
 */
class ChunkErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, isChunkError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        const isChunkError =
            error?.name === 'ChunkLoadError' ||
            error?.message?.includes('Loading chunk') ||
            error?.message?.includes('Loading CSS chunk') ||
            error?.message?.includes("Unexpected token '<'") ||
            error?.message?.includes("dynamically imported module") ||
            error?.message?.includes("failed to fetch dynamically imported module");

        return {
            hasError: true,
            isChunkError: Boolean(isChunkError),
            errorName: error?.name,
            errorMessage: error?.message || 'Unknown error'
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        if (this.state.isChunkError) {
            // Prevent infinite reload loop: only auto-reload once per 10 seconds
            const lastReload = sessionStorage.getItem('chunk_error_reload');
            const now = Date.now();

            if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
                sessionStorage.setItem('chunk_error_reload', now.toString());
                window.location.reload();
                return;
            }
        }

        console.error('ChunkErrorBoundary caught an application error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            // 1. True Chunk Load Error: New version deployed on server
            if (this.state.isChunkError) {
                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        fontFamily: '"Lexend", "Segoe UI", sans-serif',
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                        padding: '24px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '48px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                            maxWidth: '480px'
                        }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
                            <h2 style={{ color: '#2c3e50', marginBottom: '12px', fontSize: '20px' }}>
                                Phiên bản mới đã được cập nhật!
                            </h2>
                            <p style={{ color: '#7f8c8d', marginBottom: '24px', lineHeight: 1.6 }}>
                                Ứng dụng đã được nâng cấp lên phiên bản mới. 
                                Vui lòng tải lại trang để sử dụng phiên bản mới nhất.
                            </p>
                            <button
                                onClick={() => {
                                    sessionStorage.removeItem('chunk_error_reload');
                                    window.location.reload();
                                }}
                                style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 32px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                                }}
                                onMouseOver={(e) => {
                                    (e.target as HTMLButtonElement).style.transform = 'translateY(-2px)';
                                }}
                                onMouseOut={(e) => {
                                    (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
                                }}
                            >
                                Tải lại trang
                            </button>
                        </div>
                    </div>
                );
            }

            // 2. Regular JavaScript error in a component
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    fontFamily: '"Lexend", "Segoe UI", sans-serif',
                    background: '#f8fafc',
                    padding: '24px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '40px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                        maxWidth: '520px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                        <h2 style={{ color: '#1e293b', marginBottom: '12px', fontSize: '20px' }}>
                            Đã xảy ra lỗi hiển thị giao diện
                        </h2>
                        <p style={{ color: '#64748b', marginBottom: '24px', lineHeight: 1.6, fontSize: '14px' }}>
                            Hệ thống gặp sự cố không mong muốn trong khi xử lý dữ liệu. Bạn có thể tải lại trang hoặc quay về trang chủ.
                        </p>
                        
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => {
                                    window.location.href = '/';
                                }}
                                style={{
                                    background: '#f1f5f9',
                                    color: '#334155',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    padding: '10px 24px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Quay về trang chủ
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                style={{
                                    background: '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '10px 24px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                                }}
                            >
                                Tải lại trang
                            </button>
                        </div>

                        {this.state.errorMessage && (
                            <details style={{ marginTop: '24px', textAlign: 'left', fontSize: '12px', color: '#94a3b8' }}>
                                <summary style={{ cursor: 'pointer', outline: 'none' }}>Chi tiết kỹ thuật</summary>
                                <pre style={{ marginTop: '8px', padding: '8px', background: '#f1f5f9', borderRadius: '4px', overflowX: 'auto', whiteSpace: 'pre-wrap', color: '#dc2626' }}>
                                    {this.state.errorName}: {this.state.errorMessage}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ChunkErrorBoundary;
